import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';

import Button from '../../components/ui/Button';
import SessionsTable from './components/SessionsTable';
import SessionStatistics from './components/SessionStatistics';
import SessionFilters from './components/SessionFilters';
import LogoutConfirmationModal from './components/LogoutConfirmationModal';
import { sessionManagementService } from '../../lib/sessionManagementService';

const SessionManagementCenter = () => {
  const { user, userProfile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutAction, setLogoutAction] = useState(null);
  const [filters, setFilters] = useState({
    deviceType: 'all',
    status: 'active',
    searchTerm: ''
  });

  useEffect(() => {
    if (user?.id) {
      loadSessionData();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [sessions, filters]);

  const loadSessionData = async () => {
    setLoading(true);
    try {
      const { data: allSessions } = await sessionManagementService?.getAllUserSessions(user?.id, 100);
      setSessions(allSessions || []);

      const { data: stats } = await sessionManagementService?.getSessionStatistics(user?.id);
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...sessions];

    // Filter by device type
    if (filters?.deviceType !== 'all') {
      filtered = filtered?.filter(s => s?.device_type === filters?.deviceType);
    }

    // Filter by status
    if (filters?.status === 'active') {
      filtered = filtered?.filter(s => s?.is_active === true);
    } else if (filters?.status === 'inactive') {
      filtered = filtered?.filter(s => s?.is_active === false);
    }

    // Filter by search term
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(s =>
        s?.device_name?.toLowerCase()?.includes(term) ||
        s?.ip_address?.toLowerCase()?.includes(term) ||
        s?.location?.toLowerCase()?.includes(term) ||
        s?.browser_name?.toLowerCase()?.includes(term)
      );
    }

    setFilteredSessions(filtered);
  };

  const handleLogoutSession = (session) => {
    setSelectedSession(session);
    setLogoutAction('single');
    setShowLogoutModal(true);
  };

  const handleLogoutAllOther = () => {
    setLogoutAction('all-other');
    setShowLogoutModal(true);
  };

  const handleLogoutAll = () => {
    setLogoutAction('all');
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    setLoading(true);
    try {
      if (logoutAction === 'single' && selectedSession) {
        await sessionManagementService?.logoutSession(
          selectedSession?.id,
          user?.id,
          'Remote logout by user'
        );
      } else if (logoutAction === 'all-other') {
        // For demo, we'll logout all except the most recent one
        const currentSession = sessions?.find(s => s?.is_active);
        if (currentSession) {
          await sessionManagementService?.logoutAllOtherSessions(user?.id, currentSession?.id);
        }
      } else if (logoutAction === 'all') {
        await sessionManagementService?.logoutAllSessions(
          user?.id,
          'User initiated security action - all sessions terminated'
        );
      }

      await loadSessionData();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setLoading(false);
      setShowLogoutModal(false);
      setSelectedSession(null);
      setLogoutAction(null);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Session Management Center</h1>
                <p className="text-muted-foreground">
                  Monitor and manage all active login sessions across your devices
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  onClick={loadSessionData}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="warning"
                  iconName="LogOut"
                  onClick={handleLogoutAllOther}
                  disabled={loading || statistics?.totalActiveSessions <= 1}
                >
                  Logout Other Devices
                </Button>
                <Button
                  variant="danger"
                  iconName="ShieldAlert"
                  onClick={handleLogoutAll}
                  disabled={loading || statistics?.totalActiveSessions === 0}
                >
                  Logout All Sessions
                </Button>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <SessionStatistics statistics={statistics} loading={loading} />

          {/* Filters */}
          <SessionFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            totalSessions={filteredSessions?.length}
          />

          {/* Sessions Table */}
          <SessionsTable
            sessions={filteredSessions}
            loading={loading}
            onLogoutSession={handleLogoutSession}
            onRefresh={loadSessionData}
          />
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <LogoutConfirmationModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={confirmLogout}
          action={logoutAction}
          session={selectedSession}
        />
      )}
    </div>
  );
};

export default SessionManagementCenter;